import { useQuery } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { fetchApi } from '@/api/fetchApi';

interface Entitlement {
  domainId: string;
  status: string;
  grantedAt?: string;
  expiresAt?: string;
}

export function useEntitlement(tenantId: string) {
  const { data: entitlements, isLoading, error } = useQuery({
    queryKey: ['entitlements', tenantId],
    queryFn: () => fetchApi(`/domains/tenants/${tenantId}/entitlements`),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const entitlementSet = useMemo(() => {
    if (!entitlements) return new Set<string>();
    const arr = entitlements as Entitlement[];
    return new Set(arr.map(e => e.domainId));
  }, [entitlements]);

  const activeEntitlements = useMemo(() => {
    if (!entitlements) return new Set<string>();
    const arr = entitlements as Entitlement[];
    return new Set(
      arr
        .filter(e => e.status === 'Active' || e.status === 'Trial')
        .map(e => e.domainId)
    );
  }, [entitlements]);

  const hasEntitlement = useCallback((domainId: string): boolean => {
    const check = (id: string): boolean => {
      if (activeEntitlements.has(id)) return true;
      const dotIdx = id.lastIndexOf('.');
      if (dotIdx > 0) return check(id.substring(0, dotIdx));
      return false;
    };
    return check(domainId);
  }, [activeEntitlements]);

  return {
    entitlements: entitlements as Entitlement[] | undefined,
    entitlementSet,
    activeEntitlements,
    hasEntitlement,
    isLoading,
    error,
  };
}
